import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../../environments/environment';
import { LocalStorageService } from '../localStorage/local-storage.service';

export interface TmsLockEvent {
  breakType: string;
}

@Injectable({
  providedIn: 'root',
})
export class TmsSocketService implements OnDestroy {
  private localStorageService = inject(LocalStorageService);
  private ngZone = inject(NgZone);

  private socket: Socket | null = null;
  private _storageListener: ((e: StorageEvent) => void) | null = null;

  private static readonly _IS_LOCKED_KEY   = 'tms_is_locked';
  private static readonly _AWAY_OPTION_KEY = 'tms_away_option';
  private static readonly _BREAK_START_KEY = 'tms_break_start_time';

  // ── Lock state (shared across tabs via localStorage) ───
  get isLocked(): boolean {
    return localStorage.getItem(TmsSocketService._IS_LOCKED_KEY) === 'true';
  }

  // ── Away option (shared, for icon/label on any tab) ──
  get awayOption(): any | null {
    const raw = localStorage.getItem(TmsSocketService._AWAY_OPTION_KEY);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  }

  // ── Break start timestamp (for timer continuity on refresh) ───
  get breakStartTime(): number | null {
    const raw = localStorage.getItem(TmsSocketService._BREAK_START_KEY);
    return raw ? parseInt(raw, 10) : null;
  }

  saveAwayOption(option: any): void {
    localStorage.setItem(TmsSocketService._AWAY_OPTION_KEY, JSON.stringify(option));
  }

  // ── Observables ───────────────────────────────────────
  private _lockEvent$ = new BehaviorSubject<TmsLockEvent | null>(null);
  private _unlockEvent$ = new BehaviorSubject<boolean>(false);
  private _sessionPresence$ = new BehaviorSubject<any | null>(null);

  readonly lockEvent$ = this._lockEvent$.asObservable();
  readonly unlockEvent$ = this._unlockEvent$.asObservable();
  readonly sessionPresence$ = this._sessionPresence$.asObservable();

  // ── Away-lock overlay visibility ────────────────────────
  // Rendered as a fixed overlay on top of whatever page is currently active
  // (instead of routing to it) so in-progress form state on the underlying
  // page survives a lock/unlock cycle. Initialized from isLocked so a full
  // page refresh while locked shows the overlay immediately, on top of the
  // same page loading normally underneath it.
  private _overlayVisible$ = new BehaviorSubject<boolean>(this.isLocked);
  readonly overlayVisible$ = this._overlayVisible$.asObservable();

  showLockOverlay(): void {
    this._overlayVisible$.next(true);
  }

  hideLockOverlay(): void {
    this._overlayVisible$.next(false);
  }

  // ── Connect ───────────────────────────────────────────
  /**
   * Call on login. Also safe to call on app init —
   * guards against double-connect if socket already alive.
   */
  connect(): void {
    if (this.socket?.connected) return;

    const token = this.localStorageService.getKey('access_token');
    if (!token) return; // Not logged in — skip

    this.socket = io(environment.tmsBaseURL, {
      // path: '/socket.io/',
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    this.socket.on('connect', () => {
      console.log('[TMS Socket] Connected:', this.socket?.id);
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('[TMS Socket] Disconnected:', reason);
    });

    this.socket.on('connect_error', (err: Error) => {
      console.error('[TMS Socket] Connection error:', err.message);
    });

    // ── TMS Events ────────────────────────────────────
    this.socket.on('tmsLock', (payload: TmsLockEvent) => {
      localStorage.setItem(TmsSocketService._IS_LOCKED_KEY, 'true');
      // Only set start time if not already set (don't overwrite on reconnect)
      if (!localStorage.getItem(TmsSocketService._BREAK_START_KEY)) {
        localStorage.setItem(TmsSocketService._BREAK_START_KEY, Date.now().toString());
      }
      this.ngZone.run(() => {
        this._lockEvent$.next(payload);
        this._unlockEvent$.next(false);
      });
    });

    this.socket.on('tmsUnlock', () => {
      localStorage.removeItem(TmsSocketService._IS_LOCKED_KEY);
      localStorage.removeItem(TmsSocketService._BREAK_START_KEY);
      this.ngZone.run(() => {
        this._unlockEvent$.next(true);
        this._lockEvent$.next(null);
      });
    });

    // ── Cross-tab: listen for localStorage changes from other tabs ──
    this._storageListener = (e: StorageEvent) => {
      if (e.key === TmsSocketService._IS_LOCKED_KEY) {
        if (e.newValue === 'true') {
          // Another tab locked — emit lock event to trigger navigation here
          const option = this.awayOption;
          this.ngZone.run(() => {
            this._lockEvent$.next(option ? { breakType: option.key } : { breakType: '' });
            this._unlockEvent$.next(false);
          });
        } else if (e.newValue === null) {
          // Another tab unlocked
          this.ngZone.run(() => {
            this._unlockEvent$.next(true);
            this._lockEvent$.next(null);
          });
        }
      }
    };

    window.addEventListener('storage', this._storageListener);

    // ── Session presence ────────────────────────────────
    this.socket.on('session-presence', (payload: any) => {
      this.ngZone.run(() => {
        this._sessionPresence$.next(payload);
      });
    });
  }

  markLocked(startTime?: number): void {
    localStorage.setItem(TmsSocketService._IS_LOCKED_KEY, 'true');
    // Use provided timestamp or fall back to now — only set if not already present
    if (!localStorage.getItem(TmsSocketService._BREAK_START_KEY)) {
      localStorage.setItem(TmsSocketService._BREAK_START_KEY, (startTime ?? Date.now()).toString());
    }
  }

  markUnlocked(): void {
    localStorage.removeItem(TmsSocketService._IS_LOCKED_KEY);
    localStorage.removeItem(TmsSocketService._AWAY_OPTION_KEY);
    localStorage.removeItem(TmsSocketService._BREAK_START_KEY);
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    if (this._storageListener) {
      window.removeEventListener('storage', this._storageListener);
      this._storageListener = null;
    }
    localStorage.removeItem(TmsSocketService._IS_LOCKED_KEY);
    localStorage.removeItem(TmsSocketService._AWAY_OPTION_KEY);
    localStorage.removeItem(TmsSocketService._BREAK_START_KEY);
    this._lockEvent$.next(null);
    this._unlockEvent$.next(false);
    this._overlayVisible$.next(false);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}